import { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { MagnifyingGlass, ArrowsClockwise, X, CaretDown, CaretUp } from '@phosphor-icons/react';

const DEBOUNCE_MS = 500;

export function SearchFilterBar({
  fields = [],
  filters,
  setFilters,
  onSearch,
  onReset,
  onRefresh,
  loading = false,
  resultCount,
  defaultOpen = false
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [localValues, setLocalValues] = useState({});
  const debounceTimers = useRef({});

  // Sync local values when filters change externally (e.g. reset)
  useEffect(() => {
    const textFields = {};
    fields.forEach(f => {
      if (f.type !== 'select') {
        textFields[f.key] = filters[f.key] || '';
      }
    });
    setLocalValues(textFields);
  }, [filters, fields]);

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => { Object.values(timers).forEach(clearTimeout); };
  }, []);

  const handleTextChange = (key, value) => {
    setLocalValues(prev => ({ ...prev, [key]: value }));
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => {
      setFilters(prev => ({ ...prev, [key]: value }));
    }, DEBOUNCE_MS);
  };

  const hasActiveFilters = Object.entries(filters).some(([, v]) => v && v !== 'all');

  const handleReset = () => {
    const cleared = {};
    fields.forEach(f => {
      if (f.type === 'select') cleared[f.key] = f.defaultValue || 'all';
      else cleared[f.key] = '';
    });
    // Clear all pending debounce timers
    Object.values(debounceTimers.current).forEach(clearTimeout);
    debounceTimers.current = {};
    setLocalValues({});
    setFilters(cleared);
    if (onReset) onReset(cleared);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Flush any pending debounce immediately
      Object.keys(debounceTimers.current).forEach(key => {
        clearTimeout(debounceTimers.current[key]);
        delete debounceTimers.current[key];
      });
      const merged = { ...filters, ...localValues };
      setFilters(merged);
      onSearch(merged);
    }
  };

  const doRefresh = onRefresh || onSearch;

  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm" data-testid="search-filter-bar">
      <CardHeader
        className="border-b border-zinc-300 py-3 px-6 cursor-pointer hover:bg-zinc-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-bold text-zinc-950 flex items-center gap-2">
            <MagnifyingGlass size={16} weight="bold" />
            Search & Filters
            {hasActiveFilters && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">Active</span>
            )}
          </CardTitle>
          <div className="flex items-center gap-3">
            {resultCount !== undefined && (
              <span className="text-xs font-bold text-zinc-500">{resultCount} results</span>
            )}
            <Button
              variant="ghost"
              size="sm"
              data-testid="refresh-button"
              onClick={(e) => { e.stopPropagation(); doRefresh(); }}
              className="h-8 px-2 text-zinc-500 hover:text-zinc-900"
              title="Refresh data"
            >
              <ArrowsClockwise size={16} weight="bold" />
            </Button>
            {isOpen ? <CaretUp size={16} className="text-zinc-400" /> : <CaretDown size={16} className="text-zinc-400" />}
          </div>
        </div>
      </CardHeader>
      {isOpen && (
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {fields.map(field => (
              <div key={field.key} className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{field.label}</Label>
                {field.type === 'select' ? (
                  <Select value={filters[field.key] || 'all'} onValueChange={(val) => setFilters(prev => ({ ...prev, [field.key]: val }))}>
                    <SelectTrigger data-testid={`filter-${field.key}`} className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    data-testid={`filter-${field.key}`}
                    type={field.type || 'text'}
                    value={localValues[field.key] ?? filters[field.key] ?? ''}
                    onChange={(e) => handleTextChange(field.key, e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={field.placeholder || ''}
                    className="h-10"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Button
              data-testid="search-button"
              onClick={() => {
                // Flush pending debounce timers
                Object.keys(debounceTimers.current).forEach(key => {
                  clearTimeout(debounceTimers.current[key]);
                  delete debounceTimers.current[key];
                });
                const merged = { ...filters, ...localValues };
                setFilters(merged);
                onSearch(merged);
              }}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-10 px-5"
            >
              <MagnifyingGlass size={16} weight="bold" className="mr-1.5" />
              {loading ? 'Searching...' : 'Search'}
            </Button>
            {hasActiveFilters && (
              <Button
                data-testid="clear-search-button"
                onClick={handleReset}
                variant="outline"
                className="h-10 px-4 border-red-300 text-red-600 hover:bg-red-50 font-bold"
              >
                <X size={16} weight="bold" className="mr-1.5" />
                Clear Search
              </Button>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export function EmptySearchResult({ onReset, message }) {
  return (
    <div data-testid="empty-search-result" className="text-center py-16 space-y-4">
      <MagnifyingGlass size={48} className="mx-auto text-zinc-300" />
      <p className="text-lg font-bold text-zinc-400">{message || 'No matching records found'}</p>
      <p className="text-sm text-zinc-400">Try adjusting your filters or search terms</p>
      {onReset && (
        <Button
          data-testid="reset-from-empty"
          onClick={onReset}
          variant="outline"
          className="mt-2"
        >
          <ArrowsClockwise size={16} className="mr-2" />
          Reset Filters & Show All
        </Button>
      )}
    </div>
  );
}

export function LoadErrorState({ onRetry }) {
  return (
    <div data-testid="load-error-state" className="text-center py-16 space-y-4">
      <X size={48} className="mx-auto text-red-300" />
      <p className="text-lg font-bold text-red-500">Failed to load data</p>
      <p className="text-sm text-zinc-400">There was an issue loading data. Please try again.</p>
      {onRetry && (
        <Button
          data-testid="retry-load-button"
          onClick={onRetry}
          className="mt-2 bg-blue-600 hover:bg-blue-700 text-white font-bold"
        >
          <ArrowsClockwise size={16} className="mr-2" />
          Retry
        </Button>
      )}
    </div>
  );
}
